import React from 'react'
import {
  Archive,
  ChatTeardrop,
  CheckSquare,
  ClipboardText,
  CopySimple,
  Gear,
  ListBullets,
  Megaphone,
  PencilLine,
  Trophy,
  Users,
} from '@phosphor-icons/react'
import { Dropdown, Modal } from 'antd'
import { Link, useHistory } from 'react-router-dom'
import { manageTeamStore } from 'state/league/manageTeam'
import { leagueGamesStore } from 'state/league/leageGames'
import { leagueEventStore } from 'state/league/leagueEvent'
import { leagueUpdatesStore } from 'state/league/leagueUpdates'
import { leagueAttendanceStore } from 'state/league/leagueAttendance'
import {
  useArchiveLeague,
  useDuplicateLeague,
  useGetLeagueById,
} from 'api/league/LeagueCreation/leagueCreation'
import { failureMessage, successMessage } from 'api/global'

interface ManageButtonLeagueProps {
  id: string
  entityId?: string
  statusProp?: string
  // On the Spaces listing the league isn't loaded into the store and the modals
  // aren't mounted there. When true, actions first load this league into the store
  // (so the shared LeagueManageModals host has context) before opening the modal.
  fromListing?: boolean
}

const ManageButtonLeague: React.FC<ManageButtonLeagueProps> = ({
  id,
  entityId,
  statusProp,
  fromListing,
}) => {
  const history = useHistory()
  const { leagueData, setActiveTab, setLeagueData } = leagueEventStore(store => ({
    leagueData: store.leagueData,
    setActiveTab: store.setActiveTab,
    setLeagueData: store.setLeagueData,
  }))
  const { refetch: refetchLeague } = useGetLeagueById(id)

  // Load this card's league into the store (listing only), then open the modal.
  const openWithLeague = async (open: (value: boolean) => void) => {
    if (fromListing) {
      const res = await refetchLeague()
      if (res.data) setLeagueData(res.data as any)
    }
    open(true)
  }
  const { setCheckInOutModalOpen } = leagueAttendanceStore(store => ({
    setCheckInOutModalOpen: store.setCheckInOutModalOpen,
  }))
  const leagueId: string = entityId || String((leagueData as any)?._id || '')
  const leagueStatus: string = statusProp || (leagueData as any)?.status || ''
  const { setManageTeamModal, setManagePlayersModal } = manageTeamStore(
    state => ({
      setManageTeamModal: state.setManageTeamModal,
      setManagePlayersModal: state.setManagePlayersModal,
    }),
  )
  const { setSelectGameModal, setSelectGameForLogModal, setLiveLogModal } =
    leagueGamesStore(store => ({
      setSelectGameModal: store.setSelectGameModal,
      setSelectGameForLogModal: store.setSelectGameForLogModal,
      setLiveLogModal: store.setLiveLogModal,
    }))

  const { setAddUpdateModal } = leagueUpdatesStore(store => ({
    setAddUpdateModal: store.setAddUpdateModal,
  }))

  const { mutate: archiveMutate } = useArchiveLeague()
  const { mutate: duplicateMutate } = useDuplicateLeague()

  const handleArchive = () => {
    const isArchived = leagueStatus === 'archived'
    Modal.confirm({
      title: isArchived ? 'Unarchive league?' : 'Archive league?',
      content: isArchived
        ? 'This will move the league back to draft.'
        : 'This will archive the league. Staff will no longer see it in active listings.',
      okText: isArchived ? 'Unarchive' : 'Archive',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: () => {
        archiveMutate({ leagueId, targetStatus: isArchived ? 'draft' : 'archived' })
      },
    })
  }

  const handleCheckIn = () => {
    // Check-in lives on the Details Attendance tab (not a standalone modal),
    // so from the listing open the league on that tab.
    setActiveTab('6')
    setCheckInOutModalOpen(true)
    if (fromListing) history.push(`/spaces/league-detail/${id}`)
  }

  const handleDuplicate = () => {
    Modal.confirm({
      title: 'Duplicate league?',
      content: 'A copy of this league will be created as a draft.',
      okText: 'Duplicate',
      cancelText: 'Cancel',
      onOk: () => {
        duplicateMutate(leagueId)
      },
    })
  }

  const items = [
    { 
      key: 'contact',
      label: (
        <div
          className="flex align-center"
          onClick={() => history.push(`/league-chat/${id}`)}
        >
          <ChatTeardrop size={20} color="#000" />
          <h4 className="w-500 text-14 ml-10px">Contact attendees</h4>
        </div>
      ),
    },
    {
      key: 'score',
      label: (
        <div
          className="flex align-center"
          onClick={() => openWithLeague(setSelectGameModal)}
        >
          <Trophy size={20} color="#000" />
          <h4 className="w-500 text-14 ml-10px">Enter score</h4>
        </div>
      ),
    },
    {
      key: 'enter_log',
      label: (
        <div
          className="flex align-center"
          onClick={() => openWithLeague(setSelectGameForLogModal)}
        >
          <ClipboardText size={20} color="#000" />
          <h4 className="w-500 text-14 ml-10px">Enter log</h4>
        </div>
      ),
    },
    {
      key: 'live_log',
      label: (
        <div
          className="flex align-center"
          onClick={() => openWithLeague(setLiveLogModal)}
        >
          <ListBullets size={20} color="#000" />
          <h4 className="w-500 text-14 ml-10px">Live log</h4>
        </div>
      ),
    },
    {
      key: 'manage_teams',
      label: (
        <div
          className="flex align-center"
          onClick={() => openWithLeague(setManageTeamModal)}
        >
          <Gear size={20} color="#000" />
          <h4 className="w-500 text-14 ml-10px">Manage teams</h4>
        </div>
      ),
    },
    {
      key: 'manage_players',
      label: (
        <div
          className="flex align-center"
          onClick={() => openWithLeague(setManagePlayersModal)}
        >
          <Users size={20} color="#000" />
          <h4 className="w-500 text-14 ml-10px">Manage players</h4>
        </div>
      ),
    },
    {
      key: 'check_in',
      label: (
        <div className="flex align-center" onClick={handleCheckIn}>
          <CheckSquare size={20} color="#000" />
          <h4 className="w-500 text-14 ml-10px">Check-in</h4>
        </div>
      ),
    },
    {
      key: 'live_updates',
      label: (
        <div
          className="flex align-center"
          onClick={() => openWithLeague(setAddUpdateModal)}
        >
          <Megaphone
            size={20}
            color="#000"
            style={{ transform: 'rotate(45deg)' }}
          />
          <h4 className="w-500 text-14 ml-10px">Live Updates</h4>
        </div>
      ),
    },
    {
      key: 'edit',
      label: (
        <Link to={`/spaces/update-league/${id}`}>
          <div className="flex align-center">
            <PencilLine size={20} color="#000" />
            <h4 className="w-500 text-14 ml-10px">Edit league</h4>
          </div>
        </Link>
      ),
    },
    {
      key: 'archive',
      label: (
        <div className="flex align-center" onClick={handleArchive}>
          <Archive size={20} color="#000" />
          <h4 className="w-500 text-14 ml-10px">
            {leagueStatus === 'archived' ? 'Unarchive league' : 'Archive league'}
          </h4>
        </div>
      ),
    },
    {
      key: 'duplicate',
      label: (
        <div className="flex align-center" onClick={handleDuplicate}>
          <CopySimple size={20} color="#000" />
          <h4 className="w-500 text-14 ml-10px">Duplicate league</h4>
        </div>
      ),
    },
  ]

  return (
    <Dropdown
      menu={{
        items: items,
      }}
      placement="bottomLeft"
      arrow={false}
      className="mr-10px "
      overlayStyle={{ width: '250px' }}
    >
      <button className="w-500 prim-sm-btn">Manage league</button>
    </Dropdown>
  )
}

export default ManageButtonLeague
